import React from "react";
import { cn } from "../../lib/utils";
import { Search, Plus, Filter } from "lucide-react";
import { Input } from "../../internal/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../internal/primitives/select";
import type { MapCustomMapsToolbarProps } from "../../contracts/map-custom-maps";

export function MapCustomMapsToolbar({
  searchValue,
  searchPlaceholder = "Search maps...",
  scopeValue,
  scopeOptions,
  summaryLabel,
  chips = [],
  secondaryActionLabel,
  primaryActionLabel,
  onSearchChange,
  onScopeChange,
  onSecondaryAction,
  onPrimaryAction,
  className,
}: MapCustomMapsToolbarProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-4 p-4 bg-card border rounded-xl shadow-sm",
        className
      )}
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Search and Scope */}
        <div className="flex flex-1 items-center gap-3 max-w-2xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-9 h-10 bg-muted/50 border-transparent focus:bg-background focus:border-input transition-all"
            />
          </div>
          
          <div className="w-40">
            <Select value={scopeValue} onValueChange={onScopeChange}>
              <SelectTrigger className="h-10 bg-muted/50 border-transparent focus:bg-background focus:border-input">
                <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Scope" />
              </SelectTrigger>
              <SelectContent>
                {scopeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {secondaryActionLabel && (
            <button
              onClick={onSecondaryAction}
              className="inline-flex items-center justify-center rounded-lg h-10 px-4 text-sm font-medium hover:bg-muted text-muted-foreground hover:text-foreground transition-colors border"
            >
              {secondaryActionLabel}
            </button>
          )}
          {primaryActionLabel && (
            <button
              onClick={onPrimaryAction}
              className="inline-flex items-center justify-center rounded-lg h-10 px-4 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-all shadow-sm shadow-primary/20"
            >
              <Plus className="w-4 h-4 mr-2" />
              {primaryActionLabel}
            </button>
          )}
        </div>
      </div>

      {/* Bottom Row: Summary and Chips */}
      {(summaryLabel || chips.length > 0) && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-muted/50">
          <div className="flex items-center gap-4">
            {summaryLabel && (
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {summaryLabel}
              </span>
            )}
            
            <div className="flex flex-wrap gap-2">
              {chips.map((chip) => (
                <div
                  key={chip.id}
                  className={cn(
                    "inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight border shadow-sm",
                    chip.tone === "success"
                      ? "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
                      : chip.tone === "warning"
                      ? "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20"
                      : chip.tone === "danger"
                      ? "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"
                      : "bg-muted text-muted-foreground border-transparent"
                  )}
                >
                  {chip.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
